import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// INTERFACES
import { environment } from '@app/env-service';
import { PaginationInterface } from '@app/interfaces/pagination.model';

// SERVICES
import {
  UnitConversionRuleInterface,
  UnitConversionRuleType,
} from './../../../../modules/unit-measurement/interfaces/unit-measurement.interface';

@Injectable({
  providedIn: 'root',
})
export class UnitConversionRuleService {
  private readonly _unitConversionRules = new BehaviorSubject<
    UnitConversionRuleInterface[]
  >(<UnitConversionRuleInterface[]>[]);
  private readonly _currentUnitConversionRule =
    new BehaviorSubject<UnitConversionRuleInterface>(
      <UnitConversionRuleInterface>null,
    );
  private readonly _pagination = new BehaviorSubject<PaginationInterface>(
    <PaginationInterface>{},
  );
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _deleting = new BehaviorSubject<boolean>(<boolean>false);

  private unitConversionRules :UnitConversionRuleInterface[] = [];

  unitConversionRules$ = this._unitConversionRules.asObservable();
  currentUnitConversionRule$ = this._currentUnitConversionRule.asObservable();
  pagination$ = this._pagination.asObservable();
  submittingForm$ = this._submittingForm.asObservable();
  deleting$ = this._deleting.asObservable();
  loading$ = this._loading.asObservable();

  constructor(private http :HttpClient) {}

  get(params ?:{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.http
      .get(`${environment.urlV2}/uom/unit-conversion-rules`, { params })
      .subscribe(
        (response :any) => {
          this._loading.next(false);

          this.unitConversionRules = response.data;

          this._unitConversionRules.next(response.data);
          this._pagination.next(response.meta);
        },
        (response :any) => throwError(response.error),
      );
  }

  getAsync(params ?:Record<string, any>) {
    return this.http.get(`${environment.urlV2}/uom/unit-conversion-rules`, {
      params,
    });
  }

  getById(id :number) :Subscription {
    return this.http
      .get(`${environment.urlV2}/uom/unit-conversion-rules/${id}`)
      .subscribe(
        (response :any) => {
          this._currentUnitConversionRule.next(response.data);
        },
        (response :any) => throwError(response.error),
      );
  }

  create(
    unitConversionRule :Partial<UnitConversionRuleInterface>,
  ) :Observable<any> {
    this._submittingForm.next(true);

    return this.http
      .post(`${environment.urlV2}/uom/unit-conversion-rules`, unitConversionRule)
      .pipe(
        map((response :UnitConversionRuleInterface) => {
          this._unitConversionRules.next([...this.unitConversionRules, response]);
          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(response.error);
        }),
      );
  }

  update(unitConversionRule :UnitConversionRuleInterface) :Observable<any> {
    this._submittingForm.next(true);

    return this.http
      .put(
        `${environment.urlV2}/uom/unit-conversion-rules/${unitConversionRule.id}`,
        unitConversionRule,
      )
      .pipe(
        map((response :UnitConversionRuleInterface) => {
          const updatedUnitConversionRules = this.unitConversionRules.filter(
            (unitConversionRule) => unitConversionRule.id !== response.id,
          );
          updatedUnitConversionRules.push(response);
          this._unitConversionRules.next(updatedUnitConversionRules);
          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(response.error);
        }),
      );
  }

  delete(id :number) :Observable<any> {
    this._deleting.next(true);

    return this.http
      .delete(`${environment.urlV2}/uom/unit-conversion-rules/${id}`)
      .pipe(
        map((response :any) => {
          const updatedUnitConversionRules = this.unitConversionRules.filter(
            (unitConversionRule) => unitConversionRule.id !== id,
          );
          this._unitConversionRules.next(updatedUnitConversionRules);
          this._deleting.next(false);

          return response;
        }),
        catchError((response) => {
          this._deleting.next(false);

          return throwError(response.error);
        }),
      );
  }

  setSubmittingForm(status :boolean) :void {
    this._submittingForm.next(status);
  }

  getConversionRule(uomRules, lineItem, uomId) :any {
    let fromSystem :any;
    let fromRfq :any;

    for (let i = 0; i < uomRules.length; i++) {
      const uomRule = uomRules[i];
      const rfq =
        uomRule.rfqId &&
        ((lineItem.item && uomRule.rfqId === lineItem.item.id) ||
          (lineItem.item && uomRule.rfqId === lineItem.item.item_id) ||
          (lineItem.rfq_id && uomRule.rfqId === lineItem.rfq_id));
      const system = uomRule.type === UnitConversionRuleType.SYSTEM;
      const id = uomId === uomRule.convertedUnitOfMeasureId;
      const variantRfq =
        uomRule.rfqId &&
        (lineItem.item
          ? lineItem.item.parent_id && uomRule.rfqId === lineItem.item.parent_id
          : lineItem.rfq.parent_id && uomRule.rfqId === lineItem.rfq.parent_id);

      if (rfq && id) {
        fromRfq = uomRule;
        break;
      }

      if (variantRfq && id) {
        fromRfq = uomRule;
        break;
      }

      if (system && id) {
        fromSystem = uomRule;
      }
    }

    return fromRfq || fromSystem;
  }
}
